import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { CreateSalesOrderEmailResponseDto } from "../models/invoice-review/createSalesOrderEmailResponseDto";
import { FreightBillingsDetailValue } from "../models/invoice-review/FreightBillingsDetailValue";
import { FreightChargesInvoiceReviewDetailValue } from "../models/invoice-review/freightChargesInvoiceReviewDetailValue";
import { GetBillingDetailsForEmailsDto } from "../models/invoice-review/getBillingDetailsForEmailsDto";
import { GetInvoiceReviewHeaderBillingDetailsResponse } from "../models/invoice-review/getInvoiceReviewHeaderBillingDetailsResponse";
import { GetIRHeaderResponse } from "../models/invoice-review/getInvoiceReviewHeaderResponse";
import { GetIRSalesOrderLinesResponse } from "../models/invoice-review/getInvoiceReviewSalesOrderLinesResponse";
import { InvoiceReviewDetailDto } from "../models/invoice-review/InvoiceReviewDetailDto";
import { InvoiceReviewLines } from "../models/invoice-review/InvoiceReviewLines";
import { IpClientDepositDetailValue } from "../models/invoice-review/IpClientDepositDetailValue";
import { OrderNotes } from "../models/invoice-review/orderNotes";
import { ProjectInvoicePrintReviewReportRequestDto } from "../models/invoice-review/projectInvoicePrintReviewReportRequestDto";
import { ProjectInvoicePrintReviewReportResponseDto } from "../models/invoice-review/projectInvoicePrintReviewReportResponseDto";
import { ProposalListRequestDto } from "../models/invoice-review/proposalListRequestDto";
import { TrackingFreightApiRequestDto } from "../models/invoice-review/trackingFreightApiRequestDto";
import { TrackingFreightApiResponseDto } from "../models/invoice-review/trackingFreightApiResponseDto";
import { UpdateIRBillingReviewStatusRequestDto } from "../models/invoice-review/updateIRBillingReviewStatusRequestDto";
import { UpdateIRBillingReviewStatusResponseDto } from "../models/invoice-review/updateIRBillingReviewStatusResponseDto";
import { UpdateProjectInvoiceReviewDetailsRequestDto } from "../models/invoice-review/UpdateProjectInvoiceReviewDetailsRequestDto";
import { UpdateProjectInvoiceReviewDetailsResponseDto } from "../models/invoice-review/UpdateProjectInvoiceReviewDetailsResponseDto";
import { InvoiceEmail } from "../models/orders/invoicedEmail";
import { PrintPurchaseOrderDto } from "../models/orders/purchaseOrderLine";
import { SalesOrderNotes } from "../models/orders/salesOrderNotes";
import { GetUserDto } from "../models/users/users/getUserDto";
import { InvoiceProposalDto } from "../models/invoice-review/invoiceProposalDto";
import { FreightTrackingsDetailValue } from "../models/invoice-review/FreightTrackingsDetailValue";

@Injectable({
  providedIn: "root",
})
export class InvoiceReviewSummaryService {
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";

  private userManagementBaseURL = environment.baseUrl + "/User";

  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private invoicePreviewBaseURL = environment.baseUrl + "/InvoicePreview";
  private invoiceReviewBaseURL = environment.baseUrl + "/InvoiceReview";
  constructor(private _http: HttpClient) {}

  getInvoiceReviewSummary(
    invoiceReviewNumber: string
  ): Observable<GenericResponseDto<InvoiceReviewDetailDto>> {
    return this._http.get<GenericResponseDto<InvoiceReviewDetailDto>>(
      this.saleOrderManagementBaseURL +
        `/GetInvoiceReviewDetails?invoiceReviewNumber=` +
        invoiceReviewNumber
    );
  }

  getSalesOrderNotes(
    saleOrderId: string
  ): Observable<GenericResponseDto<OrderNotes[]>> {
    return this._http.get<GenericResponseDto<OrderNotes[]>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderNotes?SaleOrderId=` +
        saleOrderId
    );
  }

  getSalesOrderNotesWithTypeInvoiceProposal(
    invoiceProposalId: string
  ): Observable<GenericResponseDto<OrderNotes[]>> {
    return this._http.get<GenericResponseDto<OrderNotes[]>>(
      this.saleOrderManagementBaseURL +
        `/GetSalesOrderNotesWithTypeInvoiceProposal?InvoiceProposalId=` +
        invoiceProposalId
    );
  }

  getOrderLineDetails(
    invoiceReviewNumber: string,
    paging = 0
  ): Observable<GenericResponseDto<InvoiceReviewLines>> {
    return this._http.get<GenericResponseDto<InvoiceReviewLines>>(
      this.saleOrderManagementBaseURL +
        `/GetInvoiceReviewLineDetails?invoiceReviewNumber=${invoiceReviewNumber}&paging=${paging}`
    );
  }

  getInvoiceReviewListing(): Observable<
    GenericResponseDto<InvoiceProposalDto[]>
  > {
    return this._http.get<GenericResponseDto<InvoiceProposalDto[]>>(
      this.invoicePreviewBaseURL + `/GetInvoiceProposals`
    );
  }

  getInvoiceReviewListingPaginated(
    request: ProposalListRequestDto
  ): Observable<GenericResponseDto<InvoiceProposalDto[]>> {
    return this._http.post<GenericResponseDto<InvoiceProposalDto[]>>(
      this.invoicePreviewBaseURL + `/GetInvoiceProposalsPaginated`,
      request
    );
  }

  getInvoiceEmail(id: string): Observable<GenericResponseDto<InvoiceEmail[]>> {
    return this._http.get<GenericResponseDto<InvoiceEmail[]>>(
      this.invoicePreviewBaseURL +
        "/GetInvoiceEmailsBySalesOrderId?SalesOrderId=" +
        id
    );
  }

  getOrderTracking(
    payload: TrackingFreightApiRequestDto
  ): Observable<GenericResponseDto<TrackingFreightApiResponseDto>> {
    return this._http.post<GenericResponseDto<TrackingFreightApiResponseDto>>(
      `${this.d365SynchroniserBaseURL}/TrackingFreightAPI`,
      payload
    );
  }

  getFreightTrackingLine(
    oDataFilter: string
  ): Observable<GenericResponseDto<FreightTrackingsDetailValue[]>> {
    return this._http.get<GenericResponseDto<FreightTrackingsDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        `/GetFreightTrackings?odataFilter=${encodeURIComponent(oDataFilter)}`
    );
  }

  getBillingDetailsLine(
    oDataFilter: string
  ): Observable<GenericResponseDto<FreightBillingsDetailValue[]>> {
    return this._http.get<GenericResponseDto<FreightBillingsDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        `/GetFreightBillings?odataFilter=${encodeURIComponent(oDataFilter)}`
    );
  }

  getPurchaseOrderLine(
    SalesOrderId: string
  ): Observable<GenericResponseDto<PrintPurchaseOrderDto[]>> {
    return this._http.get<GenericResponseDto<PrintPurchaseOrderDto[]>>(
      this.saleOrderManagementBaseURL +
        "/GetPurchaseOrderPrintDetails?SaleOrderId=" +
        SalesOrderId
    );
  }

  getBillingDetailsForEmails(
    proposalId: string
  ): Observable<GenericResponseDto<GetBillingDetailsForEmailsDto>> {
    return this._http.get<GenericResponseDto<GetBillingDetailsForEmailsDto>>(
      this.saleOrderManagementBaseURL +
        "/GetBillingDetailsForEmails?proposalId=" +
        proposalId
    );
  }

  GetMyProfile(): Observable<GenericResponseDto<GetUserDto>> {
    return this._http.get<GenericResponseDto<GetUserDto>>(
      this.userManagementBaseURL + "/GetMyProfile"
    );
  }

  deleteInvoiceEmailData(
    payload: number[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.saleOrderManagementBaseURL + "/DeleteSalesOrderEmail", payload);
  }

  createInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.invoicePreviewBaseURL + "/CreateSalesOrderInvoiceEmail", payload);
  }

  updateInvoiceEmailSaleOrderHeaderData(
    payload: InvoiceEmail[]
  ): Observable<GenericResponseDto<CreateSalesOrderEmailResponseDto>> {
    return this._http.post<
      GenericResponseDto<CreateSalesOrderEmailResponseDto>
    >(this.invoicePreviewBaseURL + "/UpdateSalesOrderEmail", payload);
  }

  AddSalesOrderNotes(
    payload: SalesOrderNotes
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.saleOrderManagementBaseURL + "/AddSalesOrderNotes",
      payload
    );
  }

  GetProjectInvoicePrintReviewReport(
    payload: ProjectInvoicePrintReviewReportRequestDto
  ): Observable<
    GenericResponseDto<ProjectInvoicePrintReviewReportResponseDto>
  > {
    return this._http.post<
      GenericResponseDto<ProjectInvoicePrintReviewReportResponseDto>
    >(
      this.d365SynchroniserBaseURL + "/GetInvoiceReviewReportBySalesOrderId",
      payload
    );
  }

  getFreightChargesInvoiceReview(
    odataFilter: string
  ): Observable<GenericResponseDto<FreightChargesInvoiceReviewDetailValue[]>> {
    return this._http.get<
      GenericResponseDto<FreightChargesInvoiceReviewDetailValue[]>
    >(
      this.d365SynchroniserBaseURL +
        "/FreightCharges?odataFilter=" +
        odataFilter
    );
  }

  getIpClientDeposit(
    odataFilter: string
  ): Observable<GenericResponseDto<IpClientDepositDetailValue[]>> {
    return this._http.get<GenericResponseDto<IpClientDepositDetailValue[]>>(
      this.d365SynchroniserBaseURL +
        "/IpClientDeposit?odataFilter=" +
        odataFilter
    );
  }

  getIRSalesOrderLines(
    saleOrderId: string
  ): Observable<GenericResponseDto<GetIRSalesOrderLinesResponse>> {
    return this._http.get<GenericResponseDto<GetIRSalesOrderLinesResponse>>(
      this.d365SynchroniserBaseURL +
        "/GetIRSalesOrderLines?salesOrderId=" +
        saleOrderId
    );
  }

  getIRHeader(
    saleOrderId: string
  ): Observable<GenericResponseDto<GetIRHeaderResponse>> {
    return this._http.get<GenericResponseDto<GetIRHeaderResponse>>(
      this.d365SynchroniserBaseURL + "/GetIRHeader?salesOrderId=" + saleOrderId
    );
  }

  submitInvoiceReviewStatus(
    payload: UpdateIRBillingReviewStatusRequestDto
  ): Observable<GenericResponseDto<UpdateIRBillingReviewStatusResponseDto>> {
    return this._http.post<
      GenericResponseDto<UpdateIRBillingReviewStatusResponseDto>
    >(this.invoiceReviewBaseURL + "/UpdateIRBillingReviewStatus", payload);
  }

  updateProjectInvoiceReviewDetails(
    payload: UpdateProjectInvoiceReviewDetailsRequestDto
  ): Observable<
    GenericResponseDto<UpdateProjectInvoiceReviewDetailsResponseDto>
  > {
    return this._http.post<
      GenericResponseDto<UpdateProjectInvoiceReviewDetailsResponseDto>
    >(
      this.invoiceReviewBaseURL + "/UpdateProjectInvoiceReviewDetails",
      payload
    );
  }

  getInvoiceReviewHeaderBillingDetails(
    saleOrderId: string
  ): Observable<
    GenericResponseDto<GetInvoiceReviewHeaderBillingDetailsResponse>
  > {
    return this._http.get<
      GenericResponseDto<GetInvoiceReviewHeaderBillingDetailsResponse>
    >(
      this.invoiceReviewBaseURL +
        "/GetInvoiceReviewHeaderBillingDetails?salesOrderId=" +
        saleOrderId
    );
  }
}
